import React, { useState, useRef } from "react";
import { Booking } from "../types/booking";
import { Check, Clock, Calendar, Play, UserX, XCircle, Ban } from "lucide-react";

interface BookingCardProps {
  booking: Booking;
  style?: React.CSSProperties;
  onClick?: () => void;
  bgColor?: string;
  isLastColumn?: boolean;
  showAbove?: boolean;
}

const BookingCard: React.FC<BookingCardProps> = ({
  booking,
  style,
  onClick,
  bgColor: propBgColor,
  isLastColumn = false,
  showAbove = false,
}) => {
  const [hovered, setHovered] = useState(false);
  const [shouldOpenLeft, setShouldOpenLeft] = useState(isLastColumn);
  const cardRef = useRef<HTMLDivElement>(null);

  const checkPosition = () => {
    if (cardRef.current) {
      const rect = cardRef.current.getBoundingClientRect();
      const screenWidth = window.innerWidth;
      // If the card's right edge is within 280px of the screen edge (popover is 256px wide + margin)
      const nearRightEdge = (screenWidth - rect.right) < 280;
      setShouldOpenLeft(nearRightEdge || isLastColumn);
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date
      .toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
      .toLowerCase();
  };

  const timeRange = `${formatTime(booking.start)} - ${formatTime(booking.end)}`;

  const staffBaseColor = booking.isBlocked || booking.status === "blocked"
    ? "#6B7280"
    : (booking.categoryColor || propBgColor || "#885ABB");

  const statusConfig = {
    completed: {
      text: "Completed",
      icon: <Check size={12} strokeWidth={4} />,
    },
    pending: {
      text: "Pending",
      icon: <Clock size={12} strokeWidth={4} />,
    },
    booked: {
      text: "Booked",
      icon: <Calendar size={12} strokeWidth={4} />,
    },
    in_progress: {
      text: "In Progress",
      icon: <Play size={12} strokeWidth={4} />,
    },
    no_show: {
      text: "No Show",
      icon: <UserX size={12} strokeWidth={4} />,
    },
    cancelled: {
      text: "Cancelled",
      icon: <XCircle size={12} strokeWidth={4} />,
    },
    blocked: {
      text: "Blocked",
      icon: <Ban size={12} strokeWidth={4} />,
    },
  };

  const currentStatus = statusConfig[booking.status as keyof typeof statusConfig] || statusConfig.booked;

  const heightStr = style?.height?.toString() || "";
  const heightPx = parseInt(heightStr.replace("px", "")) || 100;

  const isUltraShort = heightPx < 45;
  const isShort = heightPx < 85;

  const topStr = style?.top?.toString() || "";
  const topPx = parseInt(topStr.replace("px", "")) || 0;

  return (
    <div
      className={`absolute shadow-none lg:shadow-sm cursor-pointer overflow-visible transition-all duration-200 border-0 md:border md:border-white/20
        ${isUltraShort
          ? "p-1 rounded-md"
          : isShort
            ? "p-1.5 rounded-md"
            : "p-3 rounded-lg"
        } 
        ${hovered ? "z-[1900] ring-2 ring-white/50" : "z-10"}`}
      style={{
        backgroundColor: staffBaseColor,
        ...style,
        zIndex: hovered ? 2000 : style?.zIndex,
      }}
      onClick={() => {
        setHovered(false);
        onClick?.();
      }}
      onMouseEnter={() => {
        checkPosition();
        setHovered(true);
      }}
      onMouseLeave={() => setHovered(false)}
      ref={cardRef}
    >
      <div
        className={`flex flex-col h-full min-w-0 ${isUltraShort ? "gap-0.5" : isShort ? "gap-0.5" : "gap-1.5"
          }`}
      >
        <div className="flex flex-col min-w-0 gap-0.5">
          <div
            className={`text-white/90 font-bold leading-tight truncate
            ${isUltraShort ? "text-[8px]" : isShort ? "text-[9px]" : "text-[10px]"}`}
          >
            {formatTime(booking.start)} - {formatTime(booking.end)}
          </div>
          <div
            className={`font-black text-white truncate uppercase tracking-tight leading-tight
            ${isUltraShort
                ? "text-[9px]"
                : isShort
                  ? "text-[11px]"
                  : "text-[13px]"
              }`}
          >
            {booking.client}
          </div>
          {!(booking.isBlocked || booking.status === "blocked") && booking.phone && (
            <div
              className={`text-white/85 font-semibold truncate leading-tight
              ${isUltraShort ? "text-[8px]" : isShort ? "text-[9px]" : "text-[10px]"}`}
            >
              {booking.phone}
            </div>
          )}
          {!(booking.isBlocked || booking.status === "blocked") && booking.service && (
            <div
              className={`text-white/80 font-medium truncate leading-tight
              ${isUltraShort ? "text-[8px]" : isShort ? "text-[9px]" : "text-[10px]"}`}
            >
              {booking.service}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between w-full mt-auto">
          {isUltraShort ? (
            <div className="text-white scale-75 origin-left">
              {currentStatus.icon}
            </div>
          ) : (
            <div
              className={`flex items-center rounded-full bg-white/10 text-white shadow-sm ring-1 ring-white/20
                ${isShort ? "gap-1 px-1.5 py-0" : "gap-1.5 px-2 py-0.5"}`}
            >
              <div className={isShort ? "scale-75 origin-left" : ""}>
                {currentStatus.icon}
              </div>
              <span
                className={`font-black uppercase tracking-wider ${isShort ? "text-[8px]" : "text-[9px]"
                  }`}
              >
                {currentStatus.text}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Hover popup kept for details without affecting card scale */}
      {hovered && (
        <div
          className={`absolute z-[2000] w-64 p-4 text-xs rounded-xl shadow-2xl backdrop-blur-md border border-white/20
            ${showAbove
              ? (topPx < 180 ? "top-full left-0 mt-2" : "bottom-full left-0 mb-2")
              : shouldOpenLeft
                ? `right-full mr-2 ${topPx > 1500 ? "bottom-0" : "top-0"}`
                : `left-full ml-2 ${topPx > 1500 ? "bottom-0" : "top-0"}`
            }`}
          style={{ backgroundColor: staffBaseColor, color: "white" }}
        >
          <div className="flex items-center gap-2 mb-2 pb-2 border-b border-white/20">
            <div className="p-1.5 bg-white/20 rounded-lg">
              {currentStatus.icon}
            </div>
            <div className="font-bold uppercase tracking-widest text-[10px]">
              {booking.isBlocked || booking.status === "blocked"
                ? "Blocked Time"
                : `${booking.status} Booking`}
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between gap-2">
              <span className="opacity-70 text-[10px] uppercase shrink-0">
                {booking.isBlocked || booking.status === "blocked" ? "Title:" : "Client:"}
              </span>
              <span className="font-bold text-right">{booking.client}</span>
            </div>
            {!(booking.isBlocked || booking.status === "blocked") && booking.phone && (
              <div className="flex justify-between gap-2">
                <span className="opacity-70 text-[10px] uppercase shrink-0">Phone:</span>
                <span className="font-bold text-right">{booking.phone}</span>
              </div>
            )}
            {!(booking.isBlocked || booking.status === "blocked") && booking.service && (
              <div className="flex justify-between gap-2">
                <span className="opacity-70 text-[10px] uppercase shrink-0">Service:</span>
                <span className="font-bold text-right">{booking.service}</span>
              </div>
            )}
            <div className="flex justify-between gap-2">
              <span className="opacity-70 text-[10px] uppercase shrink-0">Time:</span>
              <span className="font-bold text-right">{timeRange}</span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="opacity-70 text-[10px] uppercase shrink-0">Staff:</span>
              <span className="font-bold text-right">{booking.staff}</span>
            </div>
            {booking.isBlocked || booking.status === "blocked" ? (
              booking.reason ? (
                <div className="flex justify-between gap-2 pt-2 border-t border-white/20 mt-2">
                  <span className="opacity-70 text-[10px] uppercase shrink-0">Note:</span>
                  <span className="font-bold text-right max-w-[140px]">{booking.reason}</span>
                </div>
              ) : null
            ) : (
            <div className="flex justify-between gap-2 pt-2 border-t border-white/20 mt-2">
              <span className="opacity-70 text-[10px] uppercase shrink-0">Payment:</span>
              <span className="font-bold text-right">
                {booking.currency} {Number(booking.amountPaid).toFixed(2)} / {Number(booking.amountToPay).toFixed(2)}
              </span>
            </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingCard;
